/**
 * MER-487 PR C: client-side logic for the /admin customer CRUD UI.
 *
 * The trust boundary is the SECURITY DEFINER `admin_request_action`
 * RPC from migration 0041 (PR A): it re-validates the action type and
 * payload server-side and gates on is_staff(). Everything here exists
 * to give staff fast, friendly feedback BEFORE the round-trip and to
 * normalize what comes back. A caller skipping these helpers gains
 * nothing; the RPC rejects bad input on its own.
 *
 * Plain .mjs so the node:test suite imports it directly (the
 * commercialState.mjs precedent); adminActions.d.mts types it for the
 * React pages. Pure helpers (buildCreatePayload, summarizeActionResult)
 * take data in / data out; the thin data fns take the supabase client
 * as the first argument so tests can hand in a chainable mock.
 */

import { EMAIL_PATTERN, ROLE_OPTIONS, USER_ID_PATTERN } from "./tenantUsers.mjs";

/** Mirror of the slug CHECK in migration 0041: 2-63 chars, lowercase
 * letters / digits / hyphens, must start with a letter or digit. */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}$/;

/** The four real tier templates (templates/{essential,growth,professional,
 * enterprise}.yaml; essential added by #1084). MER-447: do not offer
 * tiers the build pipeline cannot scaffold. */
export const TIER_OPTIONS = ["essential", "growth", "professional", "enterprise"];

/** Platforms the connector layer can actually ingest today. */
export const PLATFORM_OPTIONS = ["navusoft"];

/** Action types accepted by admin_request_action (migration 0041;
 * the tenant_user_* types were added by migration 0044 / MER-492, and
 * tenant_user_regenerate_link by migration 0060 / MER-539). */
export const ACTION_TYPES = [
  "customer_create",
  "customer_update",
  "customer_disable",
  "access_code_set",
  "config_edit",
  "tenant_user_invite",
  "tenant_user_role_set",
  "tenant_user_revoke",
  "tenant_user_handoff_done",
  "tenant_user_regenerate_link",
];

/**
 * Core canonical service operations offered as checkboxes on the
 * create / edit forms. Labels follow the dashboard naming convention
 * (asl renders as "Residential (Side Load)"; see CLAUDE.md LOB table).
 * Anything outside this list is still valid via the free-add input;
 * the catalog lives in PLATFORM_STATE.yaml `canonical_services`.
 */
export const CORE_SERVICES = [
  { id: "front_load", label: "Front Load" },
  { id: "roll_off", label: "Roll Off" },
  { id: "asl", label: "Residential (Side Load)" },
  { id: "rl", label: "Residential (Rear Load)" },
  { id: "hook_lift", label: "Hook Lift" },
  { id: "tractor_trailer", label: "Tractor Trailer" },
];

const TERMINAL_STATUSES = new Set(["succeeded", "failed", "cancelled"]);
const KNOWN_STATUSES = new Set(["pending", "running", "succeeded", "failed", "cancelled"]);

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function trimmed(value) {
  return typeof value === "string" ? value.trim() : "";
}

/** Trim, drop empties, dedupe (first occurrence wins). Non-arrays -> []. */
function cleanStringList(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  const seen = new Set();
  for (const item of value) {
    const s = trimmed(item);
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

/**
 * Validate + normalize the create-customer form into the
 * customer_create RPC payload.
 *
 * @param {object} form  raw form values: { name, slug, tier, platform,
 *   enabled_services, divisions, access_code }
 * @returns {{ ok: true, payload: object } | { ok: false, errors: Record<string, string> }}
 */
export function buildCreatePayload(form) {
  const src = isPlainObject(form) ? form : {};
  const errors = {};

  const name = trimmed(src.name);
  if (!name) errors.name = "Customer name is required.";

  const slug = trimmed(src.slug);
  if (!slug) {
    errors.slug = "Slug is required.";
  } else if (!SLUG_PATTERN.test(slug)) {
    errors.slug =
      "Slug must be 2-63 characters: lowercase letters, numbers, and hyphens, " +
      "starting with a letter or number.";
  }

  const tier = trimmed(src.tier);
  if (!TIER_OPTIONS.includes(tier)) {
    errors.tier = "Pick one of the four available tiers.";
  }

  const platform = trimmed(src.platform);
  if (!PLATFORM_OPTIONS.includes(platform)) {
    errors.platform = "Pick a supported platform.";
  }

  const enabledServices = cleanStringList(src.enabled_services);
  if (enabledServices.length === 0) {
    errors.enabled_services = "Enable at least one service.";
  }

  const divisions = cleanStringList(src.divisions);
  const accessCode = trimmed(src.access_code);

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const payload = {
    slug,
    name,
    tier,
    platform,
    enabled_services: enabledServices,
    divisions,
  };
  if (accessCode) payload.access_code = accessCode;
  return { ok: true, payload };
}

/**
 * Normalize an admin_action row (or anything that claims to be one)
 * into what the status panel renders. Never throws: a malformed
 * result column degrades to an empty step list, never a crash while
 * staff are mid-onboarding.
 *
 * @param {unknown} actionRow
 * @returns {{
 *   status: string,
 *   terminal: boolean,
 *   steps: Array<{ step: string, status: string, detail: string | null }>,
 *   configPrUrl: string | null,
 *   error: string | null,
 * }}
 */
export function summarizeActionResult(actionRow) {
  const row = isPlainObject(actionRow) ? actionRow : {};
  const status =
    typeof row.status === "string" && KNOWN_STATUSES.has(row.status) ? row.status : "unknown";
  const terminal = TERMINAL_STATUSES.has(status);

  const result = isPlainObject(row.result) ? row.result : {};
  const rawSteps = Array.isArray(result.steps) ? result.steps : [];
  const steps = rawSteps.filter(isPlainObject).map((s) => ({
    step: typeof s.step === "string" && s.step ? s.step : "(unnamed step)",
    status: typeof s.status === "string" && s.status ? s.status : "unknown",
    detail: typeof s.detail === "string" && s.detail ? s.detail : null,
  }));

  const rawUrl = result.config_pr_url;
  const configPrUrl =
    typeof rawUrl === "string" && /^https:\/\//.test(rawUrl.trim()) ? rawUrl.trim() : null;

  const error = typeof row.error === "string" && row.error.trim() ? row.error : null;

  return { status, terminal, steps, configPrUrl, error };
}

/** Returns a problem string, or null when the payload is acceptable
 * for the given action type. Client-side mirror of the RPC checks. */
function validateActionPayload(type, payload) {
  if (!isPlainObject(payload)) return "payload must be an object";
  switch (type) {
    case "customer_create": {
      const check = buildCreatePayload(payload);
      if (!check.ok) return Object.values(check.errors).join(" ");
      return null;
    }
    case "customer_update":
    case "customer_disable": {
      if (typeof payload.slug !== "string" || !SLUG_PATTERN.test(payload.slug)) {
        return "payload.slug is missing or not a valid customer slug";
      }
      return null;
    }
    case "access_code_set": {
      if (typeof payload.slug !== "string" || !SLUG_PATTERN.test(payload.slug)) {
        return "payload.slug is missing or not a valid customer slug";
      }
      if (typeof payload.access_code !== "string" || payload.access_code.trim() === "") {
        return "payload.access_code is required";
      }
      return null;
    }
    case "config_edit": {
      if (typeof payload.slug !== "string" || !SLUG_PATTERN.test(payload.slug)) {
        return "payload.slug is missing or not a valid customer slug";
      }
      const hasTier = typeof payload.tier === "string" && TIER_OPTIONS.includes(payload.tier);
      const hasServices =
        Array.isArray(payload.enabled_services) && payload.enabled_services.length > 0;
      if (!hasTier && !hasServices) {
        return "config_edit needs a tier or a non-empty enabled_services list";
      }
      return null;
    }
    case "tenant_user_invite": {
      if (typeof payload.slug !== "string" || !SLUG_PATTERN.test(payload.slug)) {
        return "payload.slug is missing or not a valid customer slug";
      }
      if (typeof payload.email !== "string" || !EMAIL_PATTERN.test(payload.email.trim())) {
        return "payload.email is missing or not a valid email address";
      }
      if (typeof payload.role !== "string" || !ROLE_OPTIONS.includes(payload.role)) {
        return `payload.role must be one of: ${ROLE_OPTIONS.join(", ")}`;
      }
      return null;
    }
    case "tenant_user_role_set": {
      if (typeof payload.slug !== "string" || !SLUG_PATTERN.test(payload.slug)) {
        return "payload.slug is missing or not a valid customer slug";
      }
      if (typeof payload.user_id !== "string" || !USER_ID_PATTERN.test(payload.user_id)) {
        return "payload.user_id is missing or not a uuid";
      }
      if (typeof payload.role !== "string" || !ROLE_OPTIONS.includes(payload.role)) {
        return `payload.role must be one of: ${ROLE_OPTIONS.join(", ")}`;
      }
      return null;
    }
    case "tenant_user_revoke":
    case "tenant_user_handoff_done": {
      if (typeof payload.slug !== "string" || !SLUG_PATTERN.test(payload.slug)) {
        return "payload.slug is missing or not a valid customer slug";
      }
      if (typeof payload.user_id !== "string" || !USER_ID_PATTERN.test(payload.user_id)) {
        return "payload.user_id is missing or not a uuid";
      }
      return null;
    }
    case "tenant_user_regenerate_link": {
      if (typeof payload.slug !== "string" || !SLUG_PATTERN.test(payload.slug)) {
        return "payload.slug is missing or not a valid customer slug";
      }
      if (typeof payload.user_id !== "string" || !USER_ID_PATTERN.test(payload.user_id)) {
        return "payload.user_id is missing or not a uuid";
      }
      // MER-539: mirror of the migration-0060 confirm check. Strict
      // `=== true`, matching the RPC's jsonb-literal comparison and the
      // executor's `is True`; a truthy string must not mint a credential.
      if (payload.confirm_regenerate !== true) {
        return "payload.confirm_regenerate must be true (the panel confirm was not recorded)";
      }
      return null;
    }
    default:
      return `unknown action type "${type}"`;
  }
}

/**
 * Enqueue an admin action through the admin_request_action RPC.
 * No refreshSession dance here (unlike impersonation): these actions
 * do not change the caller's JWT claims, they just insert a queue row
 * the droplet poller (PR B) picks up.
 *
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 * @param {string} type     one of ACTION_TYPES
 * @param {object} payload  action payload (validated per type)
 * @returns {Promise<{ actionId: string, status: string }>}
 */
export async function requestAdminAction(supabase, type, payload) {
  if (!ACTION_TYPES.includes(type)) {
    throw new Error(`admin_request_action rejected: unknown action type "${type}"`);
  }
  const problem = validateActionPayload(type, payload);
  if (problem) {
    throw new Error(`admin_request_action rejected: ${problem}`);
  }
  const { data, error } = await supabase.rpc("admin_request_action", {
    p_action_type: type,
    p_payload: payload,
  });
  if (error) {
    throw new Error(`admin_request_action failed: ${error.message}`);
  }
  return parseRequestResult(data);
}

function parseRequestResult(raw) {
  if (!isPlainObject(raw)) {
    throw new Error("admin_request_action returned an unexpected payload");
  }
  const actionId = typeof raw.action_id === "string" && raw.action_id ? raw.action_id : null;
  const status = typeof raw.status === "string" && raw.status ? raw.status : null;
  if (!actionId || !status) {
    throw new Error("admin_request_action payload missing required fields");
  }
  return { actionId, status };
}

/**
 * Fetch one admin_action row by id (staff-read RLS; non-staff get
 * nothing back). Returns null when the row is not visible yet, which
 * the status panel treats as "keep polling".
 *
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 * @param {string} id
 */
export async function fetchAdminAction(supabase, id) {
  if (typeof id !== "string" || !id) {
    throw new Error("fetchAdminAction requires an action id");
  }
  const { data, error } = await supabase
    .from("admin_action")
    .select(
      "id, action_type, requested_by, payload, status, result, error, " +
        "created_at, started_at, completed_at",
    )
    .eq("id", id)
    .maybeSingle();
  if (error) {
    throw new Error(`fetchAdminAction failed: ${error.message}`);
  }
  return data ?? null;
}

/**
 * Last `limit` admin_audit_log entries for one customer, newest first.
 * Only the columns the activity list renders are selected.
 *
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 * @param {string} slug
 * @param {number} [limit]
 */
export async function listAuditEntries(supabase, slug, limit = 20) {
  if (typeof slug !== "string" || !slug) {
    throw new Error("listAuditEntries requires a customer slug");
  }
  const { data, error } = await supabase
    .from("admin_audit_log")
    .select("actor_email, action_type, customer_slug, occurred_at")
    .eq("customer_slug", slug)
    .order("occurred_at", { ascending: false })
    .limit(limit);
  if (error) {
    throw new Error(`listAuditEntries failed: ${error.message}`);
  }
  const rows = Array.isArray(data) ? data : [];
  return rows.filter(isPlainObject).map((r) => ({
    actorEmail: typeof r.actor_email === "string" && r.actor_email ? r.actor_email : null,
    actionType: typeof r.action_type === "string" && r.action_type ? r.action_type : "unknown",
    customerSlug: typeof r.customer_slug === "string" && r.customer_slug ? r.customer_slug : null,
    occurredAt: typeof r.occurred_at === "string" && r.occurred_at ? r.occurred_at : null,
  }));
}
